import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { motion, AnimatePresence } from 'framer-motion';
import {
  ArrowLeft,
  Download,
  Upload,
  FileSpreadsheet,
  CheckCircle2,
  AlertCircle,
  X,
  Info,
  AlertTriangle,
  Building2,
  Loader2,
  Save,
  RefreshCw
} from 'lucide-react';
import * as XLSX from 'xlsx';
import ExcelJS from 'exceljs';
import { saveAs } from 'file-saver';
import { useDropzone } from 'react-dropzone';
import { ProjectType as CheckpointProjectType } from '../../../types/checkpoint';
import { Asset } from '../../../types/asset';
import projectTypeService from '../../../services/projectTypeService';
import { assetService } from '../../../services/assetService';
import { checkpointService } from '../../../services/checkpointService';
import Button from '../../../shared/components/ui/button/Button';
import PageBreadcrumb from '../../../shared/PageBreadCrumb';
import Select from '../../../shared/components/form/Select';
import { getMaxBufferTime } from '../utils/checkpointUtils';

// Modern Clean Brand Colors
const BRAND_COLORS = {
  primary: "#339274",
  primaryHover: "#29795f",
  primaryLight: "#F8FCFA",
  primarySoft: "#EDF7F2",
  accent: "#A3CD39",
  dark: "#007F66",
  success: "#10B981",
  successLight: "#F0FDF4",
  error: "#EF4444",
  errorLight: "#FEF2F2",
  warning: "#F59E0B",
  warningLight: "#FFFBEB",
};

// Interface for parsed Excel data
interface ParsedCheckpointData {
  name: string;
  frequency: string;
  assetName: string;
  requirement?: string;
  taskType?: string;
  isInputRequired?: string;
  executionShift?: string; // Changed from executionPhase to executionShift for clarity in frontend
  executionTime?: string;
  isImageRequired?: string;
  bufferTimeDays?: number;
  errors: string[];
  rowIndex: number;
}

interface ValidationResult {
  isValid: boolean;
  validCheckpoints: ParsedCheckpointData[];
  invalidCheckpoints: ParsedCheckpointData[];
  totalRows: number;
}

export default function BulkUploadCheckpointsPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  // ========== State Management ==========
  const [projectTypes, setProjectTypes] = useState<CheckpointProjectType[]>([]);
  const [assets, setAssets] = useState<Asset[]>([]);
  const [selectedProjectType, setSelectedProjectType] = useState<string>('');
  const [isLoadingProjectTypes, setIsLoadingProjectTypes] = useState(true);
  const [isLoadingAssets, setIsLoadingAssets] = useState(false);

  // File upload state
  const [uploadedFile, setUploadedFile] = useState<File | null>(null);
  const [isProcessingFile, setIsProcessingFile] = useState(false);
  const [isGeneratingTemplate, setIsGeneratingTemplate] = useState(false);
  const [parsedData, setParsedData] = useState<ParsedCheckpointData[]>([]);
  const [validationResult, setValidationResult] = useState<ValidationResult | null>(null);

  // Submission state
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [apiError, setApiError] = useState<string | null>(null);
  const [creationResult, setCreationResult] = useState<{
    totalCreated: number;
    totalFailed: number;
    totalSubmitted: number;
    errors?: Array<{ name: string; error: string; assetName: string }>;
  } | null>(null);

  // Issues management state
  const [issuesFile, setIssuesFile] = useState<File | null>(null);
  const [isProcessingIssuesFile, setIsProcessingIssuesFile] = useState(false);
  const [showIssuesUpload, setShowIssuesUpload] = useState(false);

  // ========== Effects ==========

  // Fetch project types and categories on mount
  useEffect(() => {
    const fetchProjectTypes = async () => {
      try {
        setIsLoadingProjectTypes(true);
        const response = await projectTypeService.getAllProjectTypes();
        const activeProjectTypes = response.filter(pt => pt.status === 'active');

        const convertedProjectTypes: CheckpointProjectType[] = activeProjectTypes.map(pt => ({
          _id: pt._id,
          name: pt.name,
          description: pt.description,
          status: pt.status,
          sequence: pt.sequence,
          displayNumber: pt.displayNumber,
          createdAt: pt.createdAt || '', // Ensure string
          updatedAt: pt.updatedAt || '', // Ensure string
          createdBy: typeof pt.createdBy === 'string' ? undefined : pt.createdBy,
          updatedBy: typeof pt.updatedBy === 'string' ? undefined : pt.updatedBy,
        })) as unknown as CheckpointProjectType[]; // Force cast if needed

        setProjectTypes(convertedProjectTypes);

        // Set initial project type from URL params if available
        const urlProjectTypeId = searchParams.get('projectTypeId');
        if (urlProjectTypeId && convertedProjectTypes.some(pt => pt._id === urlProjectTypeId)) {
          setSelectedProjectType(urlProjectTypeId);
        }
      } catch (error) {
        console.error('Failed to fetch project types:', error);
        toast.error('Failed to load project types');
      } finally {
        setIsLoadingProjectTypes(false);
      }
    };


    fetchProjectTypes();
  }, [searchParams]);

  // Fetch assets when project type changes
  useEffect(() => {
    const fetchAssets = async () => {
      if (!selectedProjectType) {
        setAssets([]);
        return;
      }

      try {
        setIsLoadingAssets(true);
        // Fetch ALL assets with limit=100 (backend max)
        const response = await assetService.getAssetsByProjectType(selectedProjectType, { limit: 100 });

        let assetsData: Asset[] = [];
        if (response && typeof response === 'object' && 'assets' in response) {
          assetsData = response.assets;
        } else if (Array.isArray(response)) {
          assetsData = response;
        } else if (response && typeof response === 'object' && 'data' in response) {
          const data = (response as any).data;
          if (Array.isArray(data)) {
            assetsData = data;
          } else if (data && Array.isArray(data.docs)) {
            assetsData = data.docs;
          }
        }

        const activeAssets = assetsData.filter(asset => asset.status === 'active');
        setAssets(activeAssets);

      } catch (error) {
        console.error('Failed to fetch assets:', error);
        toast.error('Failed to load assets');
        setAssets([]);
      } finally {
        setIsLoadingAssets(false);
      }
    };

    fetchAssets();
  }, [selectedProjectType, searchParams]);

  // ========== Excel Template Generation ==========

  const generateExcelTemplate = useCallback(async () => {
    if (!selectedProjectType) {
      toast.error('Please select Project Type before downloading template');
      return;
    }

    try {
      setIsGeneratingTemplate(true);
      const selectedProjectTypeName = projectTypes.find(pt => pt._id === selectedProjectType)?.name || 'Unknown';

      // Get all assets for the selected project type
      const availableAssets = assets.filter(asset => asset.status === 'active');
      const assetNames = availableAssets.map(asset => asset.name);

      // Options Lists
      const frequencyOptions = ['Daily', 'Weekly', 'Fortnightly', 'Monthly', 'Quarterly', 'Half-Yearly', 'Yearly'];
      const taskTypeOptions = ['Visual', 'Hearing', 'Manual'];
      const executionShiftOptions = ['Pre-Gen (AM)', 'Standard (Day)', 'Post-Gen (PM)'];
      const booleanOptions = ['Yes', 'No'];

      // Create Workbook and Sheets
      const workbook = new ExcelJS.Workbook();
      const ws = workbook.addWorksheet('Checkpoint Template');
      const wsMaster = workbook.addWorksheet('MasterData', { state: 'hidden' });

      // --- Populate Master Data Sheet ---
      wsMaster.getCell('A1').value = 'Assets';
      wsMaster.getCell('B1').value = 'Frequency';
      wsMaster.getCell('C1').value = 'TaskType';
      wsMaster.getCell('D1').value = 'ExecutionShift';
      wsMaster.getCell('E1').value = 'Boolean';

      assetNames.forEach((name, index) => { wsMaster.getCell(`A${index + 2}`).value = name; });
      frequencyOptions.forEach((opt, index) => { wsMaster.getCell(`B${index + 2}`).value = opt; });
      taskTypeOptions.forEach((opt, index) => { wsMaster.getCell(`C${index + 2}`).value = opt; });
      executionShiftOptions.forEach((opt, index) => { wsMaster.getCell(`D${index + 2}`).value = opt; });
      booleanOptions.forEach((opt, index) => { wsMaster.getCell(`E${index + 2}`).value = opt; });

      // Define Ranges
      const assetRange = `'MasterData'!$A$2:$A$${Math.max(2, assetNames.length + 1)}`;
      const freqRange = `'MasterData'!$B$2:$B$${Math.max(2, frequencyOptions.length + 1)}`;
      const taskTypeRange = `'MasterData'!$C$2:$C$${Math.max(2, taskTypeOptions.length + 1)}`;
      const shiftRange = `'MasterData'!$D$2:$D$${Math.max(2, executionShiftOptions.length + 1)}`;
      const boolRange = `'MasterData'!$E$2:$E$${Math.max(2, booleanOptions.length + 1)}`;

      // --- Populate Main Template Sheet ---

      // Headers
      const headers = [
        'Asset Name *',
        'Checkpoint Description *',
        'Requirement',
        'Frequency *',
        'Execution Phase (Days)', // Buffer Time
        'Mode of Execution', // Task Type
        'Is Input Required?',
        'Is Image Required?',
        'Execution Shift', // Phase -> Time
      ];

      ws.getRow(1).values = headers;

      // Styling Headers
      const headerRow = ws.getRow(1);
      headerRow.font = { bold: true, color: { argb: 'FFFFFFFF' }, size: 12 };
      headerRow.fill = {
        type: 'pattern',
        pattern: 'solid',
        fgColor: { argb: 'FF339274' } // Brand Primary
      };
      headerRow.alignment = { vertical: 'middle', horizontal: 'center' };
      headerRow.height = 30;

      // Instructions Row
      const instructions = [
        'Select Asset',
        'Enter description (Required)',
        'Optional details',
        'Select Frequency',
        'Buffer Days (e.g. 1, 2)',
        'Select Task Type',
        'Select Yes/No (Yes = accepts any input)',
        'Select Yes/No',
        'Select Shift (Time Auto-fetched)'
      ];
      ws.getRow(2).values = instructions;
      const instructionRow = ws.getRow(2);
      instructionRow.font = { italic: true, color: { argb: 'FF666666' } };
      instructionRow.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFF0F8FF' } };
      instructionRow.alignment = { vertical: 'middle', horizontal: 'center' };

      // Set Column Widths
      ws.columns = [
        { width: 30 }, // Asset Name
        { width: 40 }, // Description
        { width: 40 }, // Requirement
        { width: 15 }, // Frequency
        { width: 25 }, // Execution Phase (Days)
        { width: 20 }, // Mode of Execution
        { width: 18 }, // Input Required
        { width: 18 }, // Is Image Required
        { width: 25 }, // Execution Shift
      ];

      // Add Data Validations to Rows 3-1000
      for (let i = 3; i <= 1000; i++) {
        // Asset Name (A)
        ws.getCell(`A${i}`).dataValidation = {
          type: 'list',
          allowBlank: true,
          formulae: [assetRange],
          showErrorMessage: true,
          errorTitle: 'Invalid Asset',
          error: 'Please select a valid asset from the list'
        };

        // Frequency (D)
        ws.getCell(`D${i}`).dataValidation = {
          type: 'list',
          allowBlank: true,
          formulae: [freqRange],
          showErrorMessage: true,
          errorTitle: 'Invalid Frequency',
          error: 'Please select from the list'
        };

        // Mode of Execution (F)
        ws.getCell(`F${i}`).dataValidation = {
          type: 'list',
          allowBlank: true,
          formulae: [taskTypeRange],
          showErrorMessage: true,
          errorTitle: 'Invalid Task Type',
          error: 'Please select from the list'
        };

        // Is Input Required (G)
        ws.getCell(`G${i}`).dataValidation = {
          type: 'list',
          allowBlank: true,
          formulae: [boolRange],
          showErrorMessage: true,
          errorTitle: 'Invalid Selection',
          error: 'Please select Yes or No'
        };

        // Is Image Required (H)
        ws.getCell(`H${i}`).dataValidation = {
          type: 'list',
          allowBlank: true,
          formulae: [boolRange],
          showErrorMessage: true,
          errorTitle: 'Invalid Selection',
          error: 'Select Yes or No'
        };

        // Execution Shift (I)
        ws.getCell(`I${i}`).dataValidation = {
          type: 'list',
          allowBlank: true,
          formulae: [shiftRange],
          showErrorMessage: true,
          errorTitle: 'Invalid Shift',
          error: 'Please select from the list'
        };
      }

      // Add Sample Data
      const sampleRow = ws.getRow(3);
      sampleRow.values = [
        assetNames[0] || 'Select Asset',
        'Check Hydraulic Oil Level',
        'Ensure oil level is between min and max marks',
        'Weekly',
        1,
        'Visual',
        'No',
        'No',
        'Standard (Day)'
      ];
      sampleRow.font = { italic: true, color: { argb: 'FF888888' } };

      // Generate Filename
      const timestamp = new Date().toISOString().split('T')[0];
      const filename = `Checkpoint_Template_${selectedProjectTypeName.replace(/[^a-zA-Z0-9]/g, '_')}_${timestamp}.xlsx`;

      // Save File
      const buffer = await workbook.xlsx.writeBuffer();
      saveAs(new Blob([buffer]), filename);

      toast.success('📊 Template downloaded successfully!');
    } catch (error) {
      console.error('Template generation error:', error);
      toast.error('Failed to generate template');
    } finally {
      setIsGeneratingTemplate(false);
    }
  }, [selectedProjectType, projectTypes, assets]);

  // ========== File Upload and Processing ==========

  const parseExcelFile = useCallback(async (file: File): Promise<ParsedCheckpointData[]> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();

      reader.onload = (e) => {
        try {
          const data = new Uint8Array(e.target?.result as ArrayBuffer);
          const workbook = XLSX.read(data, { type: 'array' });

          if (!workbook.SheetNames || workbook.SheetNames.length === 0) {
            throw new Error('Excel file is empty or corrupted.');
          }

          const sheetName = workbook.SheetNames[0]; // Assume first sheet is the template
          const worksheet = workbook.Sheets[sheetName];

          if (!worksheet) throw new Error('Excel file contains no data.');

          // Convert to JSON, skipping header and instruction rows (start from row 3)
          const jsonData = XLSX.utils.sheet_to_json(worksheet, {
            header: ['assetName', 'name', 'requirement', 'frequency', 'bufferTimeDays', 'taskType', 'isInputRequired', 'isImageRequired', 'executionShift'],
            range: 2
          });

          if (!jsonData || jsonData.length === 0) {
            throw new Error('No data found in Excel file. Please add checkpoint data starting from row 3.');
          }

          const parsedCheckpoints: ParsedCheckpointData[] = [];
          let emptyRowCount = 0;

          jsonData.forEach((row: any, index: number) => {
            // Skip completely empty rows
            if (!row.name && !row.frequency && !row.assetName) {
              emptyRowCount++;
              return;
            }

            // Map Execution Shift to Time
            let derivedTime = '';
            if (row.executionShift === 'Pre-Gen (AM)') derivedTime = '05:00-09:00';
            else if (row.executionShift === 'Standard (Day)') derivedTime = '09:00-18:00';
            else if (row.executionShift === 'Post-Gen (PM)') derivedTime = '18:00-22:00';

            let frequency = row.frequency ? String(row.frequency).trim() : '';
            if (frequency === 'Fortnight') frequency = 'Fortnightly';

            const checkpoint: ParsedCheckpointData = {
              name: row.name ? String(row.name).trim() : '',
              frequency,
              assetName: row.assetName ? String(row.assetName).trim() : '',
              requirement: row.requirement ? String(row.requirement).trim() : undefined,
              taskType: row.taskType ? String(row.taskType).trim() : undefined,
              isInputRequired: row.isInputRequired ? String(row.isInputRequired).trim() : undefined,
              executionShift: row.executionShift ? String(row.executionShift).trim() : undefined,
              executionTime: derivedTime, // Auto-fetched time
              isImageRequired: row.isImageRequired ? String(row.isImageRequired).trim() : undefined,
              bufferTimeDays: row.bufferTimeDays ? Number(row.bufferTimeDays) : undefined, // Explicit buffer mapping
              errors: [],
              rowIndex: index + 3
            };

            // Basic Validations
            if (!checkpoint.name) checkpoint.errors.push('Checkpoint Description is required');
            if (checkpoint.name && checkpoint.name.length > 2000) checkpoint.errors.push('Description too long (max 2000 chars)');

            const allowedFrequencies = ['Daily', 'Weekly', 'Fortnightly', 'Monthly', 'Quarterly', 'Half-Yearly', 'Yearly'];
            if (!checkpoint.frequency) {
              checkpoint.errors.push('Frequency is required');
            } else if (!allowedFrequencies.includes(checkpoint.frequency)) {
              checkpoint.errors.push(`Invalid Frequency: ${checkpoint.frequency}`);
            }

            if (!checkpoint.assetName) {
              checkpoint.errors.push('Asset Name is required');
            } else {
              const matchingAsset = assets.find(asset => asset.name === checkpoint.assetName);
              if (!matchingAsset) {
                checkpoint.errors.push(`Asset "${checkpoint.assetName}" not found in project`);
              } else if (matchingAsset.status !== 'active') {
                checkpoint.errors.push(`Asset "${checkpoint.assetName}" is inactive`);
              }
            }

            // Validate Is Input Required if provided
            if (checkpoint.isInputRequired && !['Yes', 'No'].includes(checkpoint.isInputRequired)) {
              checkpoint.errors.push('Invalid Is Input Required value. Must be Yes or No');
            }

            if (checkpoint.executionShift && !['Pre-Gen (AM)', 'Standard (Day)', 'Post-Gen (PM)'].includes(checkpoint.executionShift)) {
              checkpoint.errors.push('Invalid Execution Shift');
            }

            // Default Buffer Time Logic if not provided
            if (checkpoint.bufferTimeDays === undefined && checkpoint.frequency) {
              checkpoint.bufferTimeDays = getMaxBufferTime(checkpoint.frequency as any);
            }

            parsedCheckpoints.push(checkpoint);
          });

          if (parsedCheckpoints.length === 0) throw new Error('No valid checkpoint rows found.');
          if (parsedCheckpoints.length > 500) throw new Error('Too many checkpoints (limit 500).');

          resolve(parsedCheckpoints);
        } catch (error: any) {
          console.error('Excel parsing error:', error);
          reject(new Error(error.message || 'Failed to parse Excel file'));
        }
      };

      reader.onerror = () => reject(new Error('Failed to read file'));
      reader.readAsArrayBuffer(file);
    });
  }, [assets]);

  // Helper function to fetch all checkpoints with pagination
  const fetchAllCheckpoints = useCallback(async (projectTypeId: string): Promise<any[]> => {
    const allCheckpoints: any[] = [];
    let page = 1;
    const limit = 100; // API max limit
    let hasMore = true;

    while (hasMore) {
      try {
        const response = await checkpointService.getCheckpoints({
          projectTypeId,
          status: 'active',
          page,
          limit
        });

        const checkpoints = (response.data?.checkpoints || []) as any[];
        allCheckpoints.push(...checkpoints);

        // Check if there are more pages
        const totalPages = response.data?.pagination?.totalPages || 1;
        hasMore = page < totalPages;
        page++;
      } catch (error) {
        console.error('Error fetching checkpoints:', error);
        hasMore = false;
      }
    }

    return allCheckpoints;
  }, []);

  const validateParsedData = useCallback(async (data: ParsedCheckpointData[]): Promise<ValidationResult> => {
    const validCheckpoints = data.filter(cp => cp.errors.length === 0);
    const invalidCheckpoints = data.filter(cp => cp.errors.length > 0);

    // Additional validations (duplicates within file) - case-insensitive
    const assetCheckpointMap = new Map<string, Set<string>>();

    data.forEach(cp => {
      if (cp.errors.length > 0) return; // Skip already invalid

      // Normalize asset name and checkpoint name for comparison
      const normalizedAssetName = cp.assetName.trim();
      const normalizedCheckpointName = cp.name.trim().toLowerCase();

      if (!assetCheckpointMap.has(normalizedAssetName)) {
        assetCheckpointMap.set(normalizedAssetName, new Set());
      }
      const names = assetCheckpointMap.get(normalizedAssetName)!;
      if (names.has(normalizedCheckpointName)) {
        cp.errors.push('Duplicate checkpoint name for this asset in file');
        invalidCheckpoints.push(cp);
        // Remove from valid
        const index = validCheckpoints.indexOf(cp);
        if (index > -1) validCheckpoints.splice(index, 1);
      } else {
        names.add(normalizedCheckpointName);
      }
    });

    // Check for duplicates in database if project type is selected
    if (selectedProjectType && validCheckpoints.length > 0) {
      try {
        // Fetch all existing checkpoints for this project type using pagination
        const existingCheckpoints = await fetchAllCheckpoints(selectedProjectType);

        // Create a map of existing checkpoints: assetId -> Set of checkpoint names (case-insensitive)
        const existingCheckpointMap = new Map<string, Set<string>>();

        existingCheckpoints.forEach((existing: any) => {
          // Get asset ID from existing checkpoint (handle both populated and non-populated)
          let assetId: string | undefined;
          if (typeof existing.assetId === 'string') {
            assetId = existing.assetId;
          } else if (existing.assetId && typeof existing.assetId === 'object') {
            assetId = existing.assetId._id || existing.assetId.id;
          }

          if (assetId) {
            // Find matching asset by ID
            const asset = assets.find(a => {
              const aId = a.id || a._id;
              return aId === assetId || String(aId) === String(assetId);
            });

            if (asset) {
              const assetName = asset.name.trim();
              if (!existingCheckpointMap.has(assetName)) {
                existingCheckpointMap.set(assetName, new Set());
              }
              const checkpointName = (existing.name || existing.description || '').trim().toLowerCase();
              if (checkpointName) {
                existingCheckpointMap.get(assetName)!.add(checkpointName);
              }
            }
          }
        });

        // Check each valid checkpoint against database (case-insensitive comparison)
        validCheckpoints.forEach(cp => {
          const assetName = cp.assetName.trim();
          const checkpointName = cp.name.trim().toLowerCase();

          if (existingCheckpointMap.has(assetName)) {
            const existingNames = existingCheckpointMap.get(assetName)!;
            if (existingNames.has(checkpointName)) {
              cp.errors.push(`Checkpoint "${cp.name}" already exists for asset "${assetName}" in database`);
              invalidCheckpoints.push(cp);
              // Remove from valid
              const index = validCheckpoints.indexOf(cp);
              if (index > -1) validCheckpoints.splice(index, 1);
            }
          }
        });
      } catch (error) {
        console.error('Error checking database duplicates:', error);
        toast.error('Failed to check for duplicates. Please verify manually before submitting.');
        // Don't block validation if duplicate check fails, but show error
      }
    }

    return {
      isValid: invalidCheckpoints.length === 0,
      validCheckpoints,
      invalidCheckpoints,
      totalRows: data.length
    };
  }, [selectedProjectType, assets, fetchAllCheckpoints]);

  const processUploadedFile = useCallback(async (file: File) => {
    if (!selectedProjectType) {
      toast.error('Please select Project Type first');
      return;
    }

    try {
      setIsProcessingFile(true);
      setApiError(null);

      const parsed = await parseExcelFile(file);
      setParsedData(parsed);

      const validation = await validateParsedData(parsed);
      setValidationResult(validation);

      if (validation.isValid) {
        toast.success(`File valid! Ready to create ${validation.validCheckpoints.length} checkpoints.`);
      } else {
        toast.warning(`Found ${validation.invalidCheckpoints.length} issues in file.`);
      }
    } catch (error: any) {
      console.error('File processing error:', error);
      setApiError(error.message);
      toast.error(error.message);
    } finally {
      setIsProcessingFile(false);
    }
  }, [selectedProjectType, parseExcelFile, validateParsedData]);

  // File validation function
  const validateFile = (file: File): string | null => {
    if (file.size > 5 * 1024 * 1024) return 'File size must be < 5MB';
    const allowedTypes = [
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      'application/vnd.ms-excel'
    ];
    if (!allowedTypes.includes(file.type)) return 'Only .xlsx/.xls files allowed';
    return null;
  };

  // Dropzone configuration
  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop: (acceptedFiles) => {
      if (acceptedFiles.length > 0) {
        const file = acceptedFiles[0];
        const error = validateFile(file);
        if (error) {
          toast.error(error);
          return;
        }
        setUploadedFile(file);
        processUploadedFile(file);
      }
    },
    accept: {
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'],
      'application/vnd.ms-excel': ['.xls']
    },
    maxFiles: 1,
    disabled: !selectedProjectType || assets.length === 0,
  });

  // ========== Event Handlers ==========

  const handleSubmit = async () => {
    if (!validationResult || validationResult.validCheckpoints.length === 0 || !selectedProjectType) {
      if (validationResult?.validCheckpoints.length === 0) {
        toast.error('No valid checkpoints to submit. Please fix errors and try again.');
      }
      return;
    }

    try {
      setIsSubmitting(true);
      setApiError(null);

      // Final duplicate check from API before submission
      let checkpointsToSubmit = validationResult.validCheckpoints;

      try {
        // Fetch all existing checkpoints using pagination
        const existingCheckpoints = await fetchAllCheckpoints(selectedProjectType);
        const duplicateErrors: string[] = [];
        const validCheckpointsToSubmit: ParsedCheckpointData[] = [];

        // Create a map of existing checkpoints: assetName -> Set of checkpoint names
        const existingCheckpointMap = new Map<string, Set<string>>();

        existingCheckpoints.forEach((existing: any) => {
          let assetId: string | undefined;
          if (typeof existing.assetId === 'string') {
            assetId = existing.assetId;
          } else if (existing.assetId && typeof existing.assetId === 'object') {
            assetId = existing.assetId._id || existing.assetId.id;
          }

          if (assetId) {
            const asset = assets.find(a => {
              const aId = a.id || a._id;
              return aId === assetId || String(aId) === String(assetId);
            });

            if (asset) {
              const assetName = asset.name.trim();
              if (!existingCheckpointMap.has(assetName)) {
                existingCheckpointMap.set(assetName, new Set());
              }
              const checkpointName = (existing.name || existing.description || '').trim().toLowerCase();
              if (checkpointName) {
                existingCheckpointMap.get(assetName)!.add(checkpointName);
              }
            }
          }
        });

        // Check each checkpoint one final time
        validationResult.validCheckpoints.forEach(cp => {
          const assetName = cp.assetName.trim();
          const checkpointName = cp.name.trim().toLowerCase();

          if (existingCheckpointMap.has(assetName)) {
            const existingNames = existingCheckpointMap.get(assetName)!;
            if (existingNames.has(checkpointName)) {
              duplicateErrors.push(`"${cp.name}" for asset "${assetName}"`);
              // Mark as invalid
              cp.errors.push('Duplicate found in database during final check');
            } else {
              validCheckpointsToSubmit.push(cp);
            }
          } else {
            validCheckpointsToSubmit.push(cp);
          }
        });

        if (duplicateErrors.length > 0) {
          setIsSubmitting(false);
          toast.error(`Found ${duplicateErrors.length} duplicate(s) in database. Please refresh and re-validate.`);
          setApiError(`Duplicates found: ${duplicateErrors.slice(0, 5).join(', ')}${duplicateErrors.length > 5 ? '...' : ''}`);

          // Update validation result to mark duplicates as invalid
          const updatedValidationResult = {
            ...validationResult,
            validCheckpoints: validCheckpointsToSubmit,
            invalidCheckpoints: [
              ...validationResult.invalidCheckpoints,
              ...validationResult.validCheckpoints.filter(cp =>
                !validCheckpointsToSubmit.includes(cp)
              )
            ]
          };
          setValidationResult(updatedValidationResult);
          return;
        }

        // Use only non-duplicate checkpoints
        if (validCheckpointsToSubmit.length === 0) {
          setIsSubmitting(false);
          toast.error('All checkpoints are duplicates. Please check the database.');
          return;
        }

        checkpointsToSubmit = validCheckpointsToSubmit;
      } catch (error) {
        console.error('Error in final duplicate check:', error);
        toast.warning('Could not verify duplicates. Proceeding with submission...');
        // Continue with original checkpoints if check fails
      }

      // Group by Asset ID - use filtered checkpoints
      const checkpointsByAsset = new Map<string, ParsedCheckpointData[]>();

      checkpointsToSubmit.forEach(cp => {
        const asset = assets.find(a => a.name.trim() === cp.assetName.trim());
        if (asset) {
          const assetId = asset.id || asset._id;
          if (!checkpointsByAsset.has(assetId)) checkpointsByAsset.set(assetId, []);
          checkpointsByAsset.get(assetId)!.push(cp);
        }
      });

      let totalCreated = 0;
      let totalFailed = 0;
      const errors: Array<{ name: string; error: string; assetName: string }> = [];

      for (const [assetId, checkpoints] of checkpointsByAsset) {
        try {
          const items = checkpoints.map(cp => ({
            name: cp.name,
            frequency: cp.frequency as any,
            requirement: cp.requirement,
            // Map new fields
            description: cp.name, // Use name as description
            taskType: cp.taskType ? [cp.taskType] as ('Visual' | 'Hearing' | 'Manual')[] : undefined, // Cast to expected union
            // Map Yes/No to boolean: Yes means true, No means false
            isInputRequired: (cp.isInputRequired === 'Yes' ? 'Both' : 'None') as any,
            executionPhase: cp.executionShift,
            executionTime: cp.executionTime,
            isImageRequired: cp.isImageRequired === 'Yes',
            bufferTimeDays: cp.bufferTimeDays || 0,
            status: 'active' as const
          }));

          const response = await checkpointService.createBulkCheckpoints({
            projectTypeId: selectedProjectType,
            assetId,
            items
          });

          if (response.success) {
            // Handle both response structures: object with totalCreated or array of created items
            if (Array.isArray(response.data)) {
              // Response data is an array of created checkpoints
              totalCreated += response.data.length;
            } else if (response.data && typeof response.data === 'object') {
              // Response data is an object - check for totalCreated or created array
              if ('totalCreated' in response.data) {
                totalCreated += (response.data as any).totalCreated || 0;
                if ((response.data as any).totalFailed > 0) {
                  totalFailed += (response.data as any).totalFailed;

                  // Capture specific failed items
                  if (Array.isArray((response.data as any).failed)) {
                    (response.data as any).failed.forEach((f: any) => {
                      errors.push({
                        name: f.data?.name || 'Unknown',
                        error: f.error || 'Unknown error',
                        assetName: assets.find(a => (a.id || a._id) === assetId)?.name || 'Unknown Asset'
                      });
                    });
                  }
                }
              } else if ('created' in response.data && Array.isArray((response.data as any).created)) {
                totalCreated += (response.data as any).created.length;
                if ((response.data as any).totalFailed > 0) {
                  totalFailed += (response.data as any).totalFailed;
                  // Capture specific failed items
                  if (Array.isArray((response.data as any).failed)) {
                    (response.data as any).failed.forEach((f: any) => {
                      errors.push({
                        name: f.data?.name || 'Unknown',
                        error: f.error || 'Unknown error',
                        assetName: assets.find(a => (a.id || a._id) === assetId)?.name || 'Unknown Asset'
                      });
                    });
                  }
                }
              } else {
                // Fallback: assume all items were created
                totalCreated += items.length;
              }
            } else {
              // Fallback: assume all items were created
              totalCreated += items.length;
            }
          } else {
            totalFailed += items.length;
            // Mark all items in this batch as failed with the general error
            checkpoints.forEach(cp => {
              errors.push({
                name: cp.name,
                error: response.message || 'Unknown error',
                assetName: cp.assetName
              });
            });
          }
        } catch (error: any) {
          totalFailed += checkpoints.length;
          // Mark all items in this batch as failed
          checkpoints.forEach(cp => {
            errors.push({
              name: cp.name,
              error: error.message || 'Network or Server Error',
              assetName: cp.assetName
            });
          });
        }
      }

      if (totalCreated > 0) {
        setCreationResult({
          totalCreated,
          totalFailed,
          totalSubmitted: checkpointsToSubmit.length,
          errors: errors.length > 0 ? errors : undefined
        });
        setShowSuccess(true);
        toast.success(`Created ${totalCreated} checkpoints!`);

        // Update validation result: remove valid checkpoints, keep only invalid ones
        if (validationResult.invalidCheckpoints.length > 0) {
          // Keep only invalid checkpoints in preview
          setValidationResult({
            isValid: false,
            validCheckpoints: [],
            invalidCheckpoints: validationResult.invalidCheckpoints,
            totalRows: validationResult.invalidCheckpoints.length
          });
        } else {
          // No invalid checkpoints, clear everything
          setValidationResult(null);
          setParsedData([]);
          setUploadedFile(null);
        }
      } else {
        const errorMsg = errors.map(e => `${e.assetName} - ${e.name}: ${e.error}`).join('; ');
        setApiError(`Failed to create checkpoints. ${errorMsg}`);
      }
    } catch (error: any) {
      setApiError(error.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleReset = () => {
    setUploadedFile(null);
    setParsedData([]);
    setValidationResult(null);
    setApiError(null);
  };

  const handleCancel = () => navigate('/masters/checkpoints');


  // Generate error Excel file for failed rows in template format
  const downloadErrorFile = useCallback(() => {
    if (!validationResult || validationResult.invalidCheckpoints.length === 0) {
      toast.error('No errors to download');
      return;
    }

    const selectedProjectTypeName = projectTypes.find(pt => pt._id === selectedProjectType)?.name || 'Unknown';
    const availableAssets = assets.filter(asset => asset.status === 'active');
    const assetNames = availableAssets.map(asset => asset.name);
    const frequencyOptions = ['Daily', 'Weekly', 'Fortnightly', 'Monthly', 'Quarterly', 'Half-Yearly', 'Yearly'];
    const taskTypeOptions = ['Visual', 'Hearing', 'Manual'];
    const executionShiftOptions = ['Pre-Gen (AM)', 'Standard (Day)', 'Post-Gen (PM)'];
    const booleanOptions = ['Yes', 'No'];

    // Create workbook with master data sheet
    const wb = XLSX.utils.book_new();

    // Master Data Sheet - organize data by columns
    const masterDataRows: any[][] = [
      ['Assets', 'Frequency', 'TaskType', 'ExecutionShift', 'Boolean']
    ];

    // Find max length to pad all columns
    const maxLength = Math.max(
      assetNames.length,
      frequencyOptions.length,
      taskTypeOptions.length,
      executionShiftOptions.length,
      booleanOptions.length
    );

    // Populate master data rows
    for (let i = 0; i < maxLength; i++) {
      masterDataRows.push([
        assetNames[i] || '',
        frequencyOptions[i] || '',
        taskTypeOptions[i] || '',
        executionShiftOptions[i] || '',
        booleanOptions[i] || ''
      ]);
    }

    const wsMaster = XLSX.utils.aoa_to_sheet(masterDataRows);

    // Main template sheet with headers matching the template format
    const headers = [
      'Asset Name *',
      'Checkpoint Description *',
      'Requirement',
      'Frequency *',
      'Execution Phase (Days)',
      'Mode of Execution',
      'Is Input Required?',
      'Is Image Required?',
      'Execution Shift',
      'Errors'
    ];

    // Instructions row
    const instructions = [
      'Select Asset',
      'Enter description (Required)',
      'Optional details',
      'Select Frequency',
      'Buffer Days (e.g. 1, 2)',
      'Select Task Type',
      'Select Yes/No (Yes = accepts any input)',
      'Select Yes/No',
      'Select Shift (Time Auto-fetched)',
      'Error messages - Fix these issues'
    ];

    // Invalid checkpoint rows
    const invalidRows = validationResult.invalidCheckpoints.map(cp => [
      cp.assetName || '',
      cp.name || '',
      cp.requirement || '',
      cp.frequency || '',
      cp.bufferTimeDays || '',
      cp.taskType || '',
      cp.isInputRequired || '',
      cp.isImageRequired || '',
      cp.executionShift || '',
      cp.errors.join('; ')
    ]);

    const templateData = [
      headers,
      instructions,
      ...invalidRows
    ];

    const ws = XLSX.utils.aoa_to_sheet(templateData);

    // Set column widths
    ws['!cols'] = [
      { wch: 30 }, // Asset Name
      { wch: 40 }, // Description
      { wch: 40 }, // Requirement
      { wch: 15 }, // Frequency
      { wch: 25 }, // Execution Phase (Days)
      { wch: 20 }, // Mode of Execution
      { wch: 18 }, // Is Input Required
      { wch: 18 }, // Is Image Required
      { wch: 25 }, // Execution Shift
      { wch: 60 }, // Errors
    ];

    // Style the header row
    const headerStyle = {
      font: { bold: true, color: { rgb: "FFFFFF" } },
      fill: { fgColor: { rgb: BRAND_COLORS.primary.replace('#', '') } },
      alignment: { horizontal: "center", vertical: "center" },
      border: {
        top: { style: "thin", color: { rgb: "000000" } },
        bottom: { style: "thin", color: { rgb: "000000" } },
        left: { style: "thin", color: { rgb: "000000" } },
        right: { style: "thin", color: { rgb: "000000" } }
      }
    };

    // Apply header styles
    ['A1', 'B1', 'C1', 'D1', 'E1', 'F1', 'G1', 'H1', 'I1', 'J1'].forEach(cell => {
      if (!ws[cell]) ws[cell] = { t: 's', v: '' };
      ws[cell].s = headerStyle;
    });

    // Style the instructions row
    const instructionStyle = {
      font: { italic: true, color: { rgb: "666666" }, bold: true },
      fill: { fgColor: { rgb: "FFFBEB" } },
      alignment: { horizontal: "center", vertical: "center" }
    };

    ['A2', 'B2', 'C2', 'D2', 'E2', 'F2', 'G2', 'H2', 'I2', 'J2'].forEach(cell => {
      if (!ws[cell]) ws[cell] = { t: 's', v: '' };
      ws[cell].s = instructionStyle;
    });

    // Add data validations for dropdown columns (starting from row 3)
    const dataValidations = [];

    // Asset Name validation (column A, rows 3+)
    if (assetNames.length > 0) {
      dataValidations.push({
        sqref: `A3:A${2 + invalidRows.length}`,
        type: 'list',
        formula1: `"${assetNames.join(',')}"`,
        showDropDown: true
      });
    }

    // Frequency validation (column D)
    if (frequencyOptions.length > 0) {
      dataValidations.push({
        sqref: `D3:D${2 + invalidRows.length}`,
        type: 'list',
        formula1: `"${frequencyOptions.join(',')}"`,
        showDropDown: true
      });
    }

    // Task Type validation (column F)
    if (taskTypeOptions.length > 0) {
      dataValidations.push({
        sqref: `F3:F${2 + invalidRows.length}`,
        type: 'list',
        formula1: `"${taskTypeOptions.join(',')}"`,
        showDropDown: true
      });
    }

    // Is Input Required validation (column G)
    if (booleanOptions.length > 0) {
      dataValidations.push({
        sqref: `G3:G${2 + invalidRows.length}`,
        type: 'list',
        formula1: `"${booleanOptions.join(',')}"`,
        showDropDown: true
      });
    }

    // Is Image Required validation (column H)
    if (booleanOptions.length > 0) {
      dataValidations.push({
        sqref: `H3:H${2 + invalidRows.length}`,
        type: 'list',
        formula1: `"${booleanOptions.join(',')}"`,
        showDropDown: true
      });
    }

    // Execution Shift validation (column I)
    if (executionShiftOptions.length > 0) {
      dataValidations.push({
        sqref: `I3:I${2 + invalidRows.length}`,
        type: 'list',
        formula1: `"${executionShiftOptions.join(',')}"`,
        showDropDown: true
      });
    }

    if (dataValidations.length > 0) {
      ws['!dataValidation'] = dataValidations;
    }

    // Add worksheets to workbook
    XLSX.utils.book_append_sheet(wb, ws, 'Checkpoint Template');
    XLSX.utils.book_append_sheet(wb, wsMaster, 'MasterData');

    // Generate filename with timestamp
    const timestamp = new Date().toISOString().split('T')[0];
    const filename = `Checkpoint_Errors_${selectedProjectTypeName.replace(/[^a-zA-Z0-9]/g, '_')}_${timestamp}.xlsx`;

    // Download file
    XLSX.writeFile(wb, filename);
    toast.success('Error file downloaded in template format! Fix errors and re-upload.');

    // Show issues upload option after downloading
    setShowIssuesUpload(true);
  }, [validationResult, projectTypes, selectedProjectType, assets]);

  // Generate correctable issues file for user to fix
  const downloadIssuesFile = useCallback(() => {
    if (!validationResult || validationResult.invalidCheckpoints.length === 0) {
      toast.error('No issues to download');
      return;
    }

    const selectedProjectTypeName = projectTypes.find(pt => pt._id === selectedProjectType)?.name || 'Unknown';

    // Get all assets for the selected project type for dropdown
    const availableAssets = assets.filter(asset => asset.status === 'active');
    const assetNames = availableAssets.map(asset => asset.name);
    const frequencyOptions = ['Daily', 'Weekly', 'Fortnightly', 'Monthly', 'Quarterly', 'Half-Yearly', 'Yearly'];

    // Create template data with the invalid checkpoints for correction
    const templateData = [
      // Header row
      ['Checkpoint Name', 'Frequency', 'Asset Name', 'Requirement (Optional)', 'Original Errors'],
      // Instructions row
      ['Fix the data below and remove this row before uploading', 'Select from Asset Reference Sheet', 'Select from Asset Reference Sheet', 'Enter detailed requirements (optional)', 'These were the original errors'],
      // Invalid checkpoint rows for correction
      ...validationResult.invalidCheckpoints.map(cp => [
        cp.name || '',
        cp.frequency || 'Weekly',
        cp.assetName || '',
        cp.requirement || '',
        cp.errors.join('; ')
      ])
    ];

    // Create workbook
    const wb = XLSX.utils.book_new();
    const ws = XLSX.utils.aoa_to_sheet(templateData);

    // Set column widths
    ws['!cols'] = [
      { wch: 50 }, // Checkpoint Name
      { wch: 20 }, // Frequency
      { wch: 35 }, // Asset Name
      { wch: 60 }, // Requirement
      { wch: 60 }, // Original Errors
    ];

    // Style the header row
    const headerStyle = {
      font: { bold: true, color: { rgb: "FFFFFF" } },
      fill: { fgColor: { rgb: BRAND_COLORS.primary.replace('#', '') } },
      alignment: { horizontal: "center", vertical: "center" },
      border: {
        top: { style: "thin", color: { rgb: "000000" } },
        bottom: { style: "thin", color: { rgb: "000000" } },
        left: { style: "thin", color: { rgb: "000000" } },
        right: { style: "thin", color: { rgb: "000000" } }
      }
    };

    // Apply header styles
    ['A1', 'B1', 'C1', 'D1', 'E1'].forEach(cell => {
      if (!ws[cell]) ws[cell] = { t: 's', v: '' };
      ws[cell].s = headerStyle;
    });

    // Style the instructions row
    const instructionStyle = {
      font: { italic: true, color: { rgb: "666666" }, bold: true },
      fill: { fgColor: { rgb: "FFFBEB" } },
      alignment: { horizontal: "center", vertical: "center" },
      border: {
        top: { style: "thin", color: { rgb: "CCCCCC" } },
        bottom: { style: "thin", color: { rgb: "CCCCCC" } },
        left: { style: "thin", color: { rgb: "CCCCCC" } },
        right: { style: "thin", color: { rgb: "CCCCCC" } }
      }
    };

    ['A2', 'B2', 'C2', 'D2', 'E2'].forEach(cell => {
      if (!ws[cell]) ws[cell] = { t: 's', v: '' };
      ws[cell].s = instructionStyle;
    });

    // Create data validation arrays
    const dataValidations = [];

    // Add data validation for frequency column
    if (frequencyOptions.length > 0) {
      dataValidations.push({
        sqref: 'B3:B1000',
        type: 'list',
        formula1: `"${frequencyOptions.join(',')}"`,
        showDropDown: true,
        showErrorMessage: true,
        errorTitle: 'Invalid Frequency',
        error: 'Please select a valid frequency from the dropdown list.',
        promptTitle: 'Frequency Selection',
        prompt: 'Select the frequency for this checkpoint from the dropdown'
      });
    }

    // Add data validation for asset column
    if (assetNames.length > 0) {
      dataValidations.push({
        sqref: 'C3:C1000',
        type: 'list',
        formula1: `"${assetNames.join(',')}"`,
        showDropDown: true,
        showErrorMessage: true,
        errorTitle: 'Invalid Asset',
        error: 'Please select a valid asset from the dropdown list.',
        promptTitle: 'Asset Selection',
        prompt: 'Select the asset for this checkpoint from the dropdown'
      });
    }

    // Apply data validations
    if (dataValidations.length > 0) {
      ws['!dataValidation'] = dataValidations;
    }

    // Add worksheet to workbook
    XLSX.utils.book_append_sheet(wb, ws, 'Issues to Fix');

    // Add instructions sheet
    const instructionsData = [
      ['📋 Fix Issues and Re-upload Instructions'],
      [''],
      ['📊 Project Information:'],
      ['Project Type:', selectedProjectTypeName],
      ['Issues Found:', validationResult.invalidCheckpoints.length + ' rows need correction'],
      [''],
      ['🔧 How to Fix Issues:'],
      [''],
      ['1️⃣ Review each row and fix the errors mentioned in the "Original Errors" column'],
      ['2️⃣ Use the dropdown menus for Frequency and Asset Name columns'],
      ['3️⃣ Ensure Checkpoint Names are unique for each asset'],
      ['4️⃣ Remove the instruction row (row 2) before uploading'],
      ['5️⃣ Remove the "Original Errors" column (column E) before uploading'],
      ['6️⃣ Save the file and upload it using the "Upload Fixed Issues" option'],
      [''],
      ['⚠️ Important Notes:'],
      ['• Fix all validation errors mentioned in the "Original Errors" column'],
      ['• Do not change the header row structure (except removing "Original Errors")'],
      ['• Make sure all required fields are filled'],
      ['• Checkpoint names must be unique within each asset'],
      ['• Asset names must match exactly with available assets'],
      [''],
      ['✅ After Fixing:'],
      ['• Remove row 2 (instructions) and column E (Original Errors)'],
      ['• Upload the fixed file using the "Upload Fixed Issues" button'],
      ['• The system will re-validate and process the corrected data'],
    ];

    const instructionsWs = XLSX.utils.aoa_to_sheet(instructionsData);
    instructionsWs['!cols'] = [{ wch: 80 }];

    // Style the instructions sheet
    if (instructionsWs['A1']) {
      instructionsWs['A1'].s = {
        font: { bold: true, size: 18, color: { rgb: BRAND_COLORS.primary.replace('#', '') } },
        alignment: { horizontal: "center" },
        fill: { fgColor: { rgb: BRAND_COLORS.primaryLight.replace('#', '') } }
      };
    }

    XLSX.utils.book_append_sheet(wb, instructionsWs, 'Fix Instructions');

    // Generate filename with timestamp
    const timestamp = new Date().toISOString().split('T')[0];
    const filename = `Checkpoint_Issues_to_Fix_${selectedProjectTypeName.replace(/[^a-zA-Z0-9]/g, '_')}_${timestamp}.xlsx`;

    // Download file
    XLSX.writeFile(wb, filename);
    toast.success('📊 Issues file downloaded! Fix the errors and upload the corrected file.');

    // Show issues upload option after downloading
    setShowIssuesUpload(true);
  }, [validationResult, projectTypes, selectedProjectType, assets]);

  // Process fixed issues file
  const processIssuesFile = useCallback(async (file: File) => {
    try {
      setIsProcessingIssuesFile(true);
      setApiError(null);

      const parsed = await parseExcelFile(file);

      const validation = await validateParsedData(parsed);
      setValidationResult(validation);

      if (validation.isValid) {
        toast.success(`Fixed issues processed successfully! ${validation.validCheckpoints.length} valid checkpoints found.`);
        // Hide the original upload and show only the fixed data
        setUploadedFile(null);
        setParsedData([]);
      } else {
        toast.warning(`Fixed issues processed with remaining errors. ${validation.invalidCheckpoints.length} invalid rows found.`);
      }
    } catch (error: any) {
      console.error('Issues file processing error:', error);
      setApiError(error.message || 'Failed to process fixed issues file');
      toast.error(error.message || 'Failed to process fixed issues file');
    } finally {
      setIsProcessingIssuesFile(false);
    }
  }, [parseExcelFile, validateParsedData]);

  // Dropzone configuration for issues file
  const { getRootProps: getIssuesRootProps, getInputProps: getIssuesInputProps, isDragActive: isIssuesDragActive } = useDropzone({
    onDrop: (acceptedFiles, rejectedFiles) => {
      // Handle rejected files
      if (rejectedFiles.length > 0) {
        const rejectedFile = rejectedFiles[0];
        const errors = rejectedFile.errors.map(error => {
          switch (error.code) {
            case 'file-too-large':
              return 'File size must be less than 5MB';
            case 'file-invalid-type':
              return 'Please upload only Excel files (.xlsx or .xls)';
            case 'too-many-files':
              return 'Please upload only one file at a time';
            default:
              return error.message;
          }
        });
        toast.error(`Issues file rejected: ${errors.join(', ')}`);
        return;
      }

      if (acceptedFiles.length > 0) {
        const file = acceptedFiles[0];

        // Additional file validation
        const validationError = validateFile(file);
        if (validationError) {
          toast.error(`Issues file validation failed: ${validationError}`);
          return;
        }

        setIssuesFile(file);
        processIssuesFile(file);
      }
    },
    accept: {
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'],
      'application/vnd.ms-excel': ['.xls']
    },
    maxFiles: 1,
    maxSize: 5 * 1024 * 1024, // 5MB limit
  });

  // Reset issues upload
  const handleResetIssues = () => {
    setIssuesFile(null);
    setShowIssuesUpload(false);
    toast.info('Issues upload reset successfully');
  };

  // Submit fixed issues
  const handleSubmitFixedIssues = async () => {
    if (!validationResult || !validationResult.isValid) {
      return;
    }

    try {
      setIsSubmitting(true);
      setApiError(null);

      // Group checkpoints by asset for individual bulk creation calls
      const checkpointsByAsset = new Map<string, ParsedCheckpointData[]>();

      validationResult.validCheckpoints.forEach(cp => {
        const asset = assets.find(a => a.name === cp.assetName);
        if (asset) {
          const assetId = asset.id || asset._id;
          if (!checkpointsByAsset.has(assetId)) {
            checkpointsByAsset.set(assetId, []);
          }
          checkpointsByAsset.get(assetId)!.push(cp);
        }
      });

      let totalCreated = 0;
      let totalFailed = 0;
      const errors: Array<{ name: string; error: string; assetName: string }> = [];

      // Create checkpoints for each asset
      for (const [assetId, checkpoints] of checkpointsByAsset) {
        try {
          const items = checkpoints.map(cp => ({
            name: cp.name,
            frequency: cp.frequency as any,
            requirement: cp.requirement,
            // Map new fields
            description: cp.name, // Use name as description
            taskType: cp.taskType ? [cp.taskType] as ('Visual' | 'Hearing' | 'Manual')[] : undefined,
            // Map Yes/No to boolean: Yes means true, No means false
            isInputRequired: (cp.isInputRequired === 'Yes' ? 'Both' : 'None') as any,
            executionPhase: cp.executionShift, // Map shift to phase
            executionTime: cp.executionTime,
            isImageRequired: cp.isImageRequired === 'Yes',
            bufferTimeDays: cp.bufferTimeDays || 0,
            status: 'active' as const
          }));

          const response = await checkpointService.createBulkCheckpoints({
            projectTypeId: selectedProjectType,
            assetId,
            items: items as any // Cast items to avoid strict typing issues with optional fields
          });

          if (response.success) {
            totalCreated += response.data.totalCreated || items.length;
            if (response.data.totalFailed > 0) {
              totalFailed += response.data.totalFailed;
              if (Array.isArray(response.data.failed)) {
                response.data.failed.forEach((f: any) => {
                  errors.push({
                    name: f.data?.name || 'Unknown',
                    error: f.error || 'Unknown error',
                    assetName: assets.find(a => (a.id || a._id) === assetId)?.name || 'Unknown Asset'
                  });
                });
              }
            }
          } else {
            totalFailed += items.length;
            const assetName = assets.find(a => (a.id || a._id) === assetId)?.name || 'Unknown Asset';
            checkpoints.forEach(cp => {
              errors.push({
                name: cp.name,
                error: response.message || 'Unknown error',
                assetName: assetName
              });
            });
          }
        } catch (error: any) {
          totalFailed += checkpoints.length;
          const assetName = assets.find(a => (a.id || a._id) === assetId)?.name || 'Unknown Asset';
          checkpoints.forEach(cp => {
            errors.push({
              name: cp.name,
              error: error.message || 'Network or Server Error',
              assetName: assetName
            });
          });
        }
      }

      if (totalCreated > 0) {
        setCreationResult({
          totalCreated,
          totalFailed,
          totalSubmitted: validationResult.validCheckpoints.length,
          errors: errors.length > 0 ? errors : undefined
        });
        setShowSuccess(true);
        let successMessage = `Successfully created ${totalCreated} checkpoints from fixed issues`;
        if (totalFailed > 0) {
          successMessage += ` (${totalFailed} failed)`;
        }
        successMessage += '!';
        toast.success(successMessage);

        // Update validation result: remove valid checkpoints, keep only invalid ones
        if (validationResult.invalidCheckpoints.length > 0) {
          // Keep only invalid checkpoints in preview
          setValidationResult({
            isValid: false,
            validCheckpoints: [],
            invalidCheckpoints: validationResult.invalidCheckpoints,
            totalRows: validationResult.invalidCheckpoints.length
          });
        } else {
          // No invalid checkpoints, clear everything
          setValidationResult(null);
          setParsedData([]);
          setUploadedFile(null);
          setIssuesFile(null);
          setShowIssuesUpload(false);
        }
      } else {
        const errorMsg = errors.map(e => `${e.assetName} - ${e.name}: ${e.error}`).join('; ');
        setApiError(`Failed to create checkpoints from fixed issues. ${errorMsg}`);
        toast.error('Failed to create any checkpoints from fixed issues');
      }
    } catch (error: any) {
      console.error('Failed to create checkpoints from fixed issues:', error);
      setApiError(error.message || 'Failed to create checkpoints from fixed issues');
      toast.error(error.message || 'Failed to create checkpoints from fixed issues');
    } finally {
      setIsSubmitting(false);
    }
  };

  // ========== Render ==========

  return (
    <div className="min-h-screen bg-gray-50 relative">
      {/* Success Modal Popup */}
      <AnimatePresence>
        {showSuccess && creationResult && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4"
            onClick={() => setShowSuccess(false)}
          >
            <motion.div
              initial={{ opacity: 0, y: 40, scale: 0.95 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: 40, scale: 0.95 }}
              transition={{ type: "spring", stiffness: 300, damping: 30 }}
              className="w-full max-w-2xl bg-white rounded-2xl shadow-2xl border border-gray-200 overflow-hidden"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Header */}
              <div className="bg-gradient-to-r from-green-500 to-green-600 px-6 py-5">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <motion.div
                      initial={{ scale: 0.7, opacity: 0 }}
                      animate={{ scale: 1, opacity: 1 }}
                      transition={{ type: "spring", stiffness: 200, damping: 18, delay: 0.1 }}
                    >
                      <CheckCircle2 className="w-10 h-10 text-white drop-shadow-lg" />
                    </motion.div>
                    <h2 className="text-2xl font-bold text-white tracking-tight">
                      Checkpoints Created Successfully!
                    </h2>
                  </div>
                  <button
                    onClick={() => setShowSuccess(false)}
                    className="text-white/80 hover:text-white transition-colors p-1 rounded-lg hover:bg-white/20"
                  >
                    <X className="w-5 h-5" />
                  </button>
                </div>
              </div>

              {/* Results Content */}
              <div className="p-6">
                <div className="space-y-4">
                  {/* Success Stats */}
                  <div className="grid grid-cols-3 gap-4">
                    <div className="bg-green-50 rounded-xl border border-green-200 p-4 text-center">
                      <div className="text-3xl font-bold text-green-700 mb-1">
                        {creationResult.totalCreated}
                      </div>
                      <div className="text-xs font-medium text-green-600 uppercase tracking-wider">
                        Created
                      </div>
                    </div>
                    <div className="bg-blue-50 rounded-xl border border-blue-200 p-4 text-center">
                      <div className="text-3xl font-bold text-blue-700 mb-1">
                        {creationResult.totalSubmitted}
                      </div>
                      <div className="text-xs font-medium text-blue-600 uppercase tracking-wider">
                        Submitted
                      </div>
                    </div>
                    {creationResult.totalFailed > 0 ? (
                      <div className="bg-red-50 rounded-xl border border-red-200 p-4 text-center">
                        <div className="text-3xl font-bold text-red-700 mb-1">
                          {creationResult.totalFailed}
                        </div>
                        <div className="text-xs font-medium text-red-600 uppercase tracking-wider">
                          Failed
                        </div>
                      </div>
                    ) : (
                      <div className="bg-gray-50 rounded-xl border border-gray-200 p-4 text-center">
                        <div className="text-3xl font-bold text-gray-400 mb-1">
                          0
                        </div>
                        <div className="text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Failed
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Summary Message */}
                  <div className="bg-gray-50 rounded-lg border border-gray-200 p-4">
                    <p className="text-sm text-gray-700 text-center">
                      {creationResult.totalCreated === creationResult.totalSubmitted ? (
                        <span className="font-medium text-green-700">
                          ✅ All {creationResult.totalCreated} checkpoints were successfully created and are now available in the system.
                        </span>
                      ) : (
                        <span>
                          <span className="font-medium text-green-700">
                            ✅ {creationResult.totalCreated} checkpoints created successfully.
                          </span>
                          {creationResult.totalFailed > 0 && (
                            <span className="text-red-600 ml-2">
                              ⚠️ {creationResult.totalFailed} checkpoints failed to create.
                            </span>
                          )}
                        </span>
                      )}
                    </p>
                  </div>

                  {/* Detailed Validation Errors */}
                  {creationResult.totalFailed > 0 && creationResult.errors && (
                    <div className="mt-4">
                      <h4 className="text-sm font-bold text-red-700 mb-2 flex items-center gap-2">
                        <AlertCircle className="w-4 h-4" />
                        Failed Items ({creationResult.totalFailed})
                      </h4>
                      <div className="bg-red-50 rounded-lg border border-red-100 max-h-48 overflow-y-auto p-2">
                        {creationResult.errors.map((err, idx) => (
                          <div key={idx} className="text-xs text-red-600 border-b border-red-100 last:border-0 py-2 px-2">
                            <div className="font-semibold">{err.assetName} - {err.name}</div>
                            <div>{err.error}</div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                </div>

                {/* Close Button */}
                <div className="mt-6 flex justify-center">
                  <Button
                    onClick={() => setShowSuccess(false)}
                    className="px-8 py-2.5 bg-green-600 hover:bg-green-700 text-white font-bold rounded-lg shadow-sm transition-all"
                  >
                    Close
                  </Button>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
      <div className="w-full px-4 py-4 mx-auto max-w-[1920px]">
        <PageBreadcrumb
          items={[
            { name: "Masters", path: "/masters" },
            { name: "Checkpoints", path: "/masters/checkpoints" },
            { name: "Bulk Upload", path: "/masters/checkpoints/bulk-upload" },
          ]}
        />

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden"
        >
          {/* Header */}
          <div className="px-6 py-4 border-b border-gray-200 bg-gray-50/50">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <div className="w-10 h-10 rounded-lg flex items-center justify-center border bg-gradient-to-br from-green-500 to-green-600 border-green-500 shadow-sm">
                  <Upload className="w-5 h-5 text-white" />
                </div>
                <div>
                  <h1 className="text-xl font-bold text-gray-900 tracking-tight">
                    Bulk Upload Checkpoints
                  </h1>
                  <p className="text-xs text-gray-500 font-medium mt-0.5">
                    Download template, fill data, and upload for bulk creation
                  </p>
                </div>
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={handleCancel}
                disabled={isSubmitting}
                className="font-medium h-9 px-4 text-xs border rounded-lg hover:bg-white hover:text-red-600 hover:border-red-200 transition-colors"
              >
                <ArrowLeft className="w-3.5 h-3.5 mr-2" />
                Back
              </Button>
            </div>
          </div>

          <div className="p-6 grid grid-cols-1 lg:grid-cols-12 gap-8">

            {/* Global API Error Alert */}
            {apiError && (
              <div className="lg:col-span-12">
                <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-xl flex items-start gap-3">
                  <AlertTriangle className="w-5 h-5 flex-shrink-0 mt-0.5" />
                  <div>
                    <h3 className="font-bold text-sm">Submission Error</h3>
                    <p className="text-sm mt-1">{apiError}</p>
                    <button
                      onClick={() => setApiError(null)}
                      className="text-xs font-semibold underline mt-2 hover:text-red-800"
                    >
                      Dismiss
                    </button>
                  </div>
                </div>
              </div>
            )}
            {/* Left Column: Instructions & Template */}
            <div className="lg:col-span-4 space-y-6">

              {/* Step 1: Project Type */}
              <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-5 relative overflow-hidden group hover:border-green-200 transition-colors">
                <div className="absolute top-0 right-0 w-24 h-24 bg-gradient-to-bl from-green-50/50 to-transparent -z-0 rounded-bl-3xl" />
                <div className="flex items-center gap-3 mb-4 relative z-10">
                  <div className="w-7 h-7 rounded-lg bg-green-50 text-green-600 flex items-center justify-center text-xs font-bold border border-green-100">1</div>
                  <h3 className="font-bold text-gray-900 text-sm">Select Project Type</h3>
                </div>

                <div className="space-y-3 relative z-10">
                  <div className="relative">
                    <Building2 className="absolute left-3 top-2.5 w-4 h-4 text-gray-400 pointer-events-none z-10" />
                    <Select
                      options={projectTypes.map(pt => ({ value: pt._id, label: pt.name }))}
                      placeholder={isLoadingProjectTypes ? "Loading..." : "Select Project Type"}
                      isLoading={isLoadingProjectTypes}
                      value={selectedProjectType ? { value: selectedProjectType, label: projectTypes.find(pt => pt._id === selectedProjectType)?.name || '' } : undefined}
                      onChange={(option) => setSelectedProjectType(option?.value || '')}
                      className="pl-9 text-sm"
                      menuPortalTarget={typeof document !== 'undefined' ? document.body : null}
                    />
                  </div>
                  <p className="text-[11px] text-gray-500 leading-relaxed">
                    Selecting a project type will load its assets into the Excel template dropdowns.
                  </p>
                </div>
              </div>

              {/* Step 2: Download Template */}
              <div className={`bg-white rounded-xl border border-gray-100 shadow-sm p-5 relative overflow-hidden group transition-all duration-300 ${!selectedProjectType ? 'opacity-60 pointer-events-none grayscale' : 'hover:border-blue-200'}`}>
                <div className="absolute top-0 right-0 w-24 h-24 bg-gradient-to-bl from-blue-50/50 to-transparent -z-0 rounded-bl-3xl" />
                <div className="flex items-center gap-3 mb-4 relative z-10">
                  <div className="w-7 h-7 rounded-lg bg-blue-50 text-blue-600 flex items-center justify-center text-xs font-bold border border-blue-100">2</div>
                  <h3 className="font-bold text-gray-900 text-sm">Download Template</h3>
                </div>

                <div className="space-y-4 relative z-10">
                  <div className="p-3 bg-blue-50/50 rounded-lg border border-blue-100 flex items-start gap-3">
                    <FileSpreadsheet className="w-8 h-8 text-blue-500 flex-shrink-0" />
                    <div>
                      <h4 className="text-xs font-bold text-blue-900">Smart Excel Template</h4>
                      <p className="text-[10px] text-blue-700 mt-1 leading-relaxed">
                        Contains dynamic dropdowns for Assets, Frequencies, Task Types, and more based on your selection.
                      </p>
                    </div>
                  </div>

                  <Button
                    onClick={generateExcelTemplate}
                    disabled={!selectedProjectType || isGeneratingTemplate}
                    className="w-full h-10 text-xs font-bold bg-blue-600 hover:bg-blue-700 text-white shadow-sm shadow-blue-200"
                  >
                    {isGeneratingTemplate ? (
                      <>
                        <Loader2 className="w-3.5 h-3.5 animate-spin mr-2" />
                        Generating...
                      </>
                    ) : (
                      <>
                        <Download className="w-3.5 h-3.5 mr-2" />
                        Download Excel Template
                      </>
                    )}
                  </Button>
                </div>
              </div>

              {/* Instructions Panel */}
              <div className="bg-gray-50 rounded-xl border border-gray-200 p-5">
                <div className="flex items-center gap-2 mb-3">
                  <Info className="w-4 h-4 text-gray-500" />
                  <h4 className="text-xs font-bold text-gray-700 uppercase tracking-wider">Instructions</h4>
                </div>
                <ul className="space-y-2">
                  {[
                    'Select Frequency from dropdown',
                    'Select Asset from dropdown',
                    'Choose Task Type (Visual/Hearing/Manual)',
                    'Select if Input Required (Yes = accepts any input)',
                    'Set Execution Phase & Time'
                  ].map((item, i) => (
                    <li key={i} className="flex items-start gap-2 text-[11px] text-gray-600">
                      <div className="w-1 h-1 rounded-full bg-gray-400 mt-1.5 flex-shrink-0" />
                      {item}
                    </li>
                  ))}
                </ul>
              </div>
            </div>

            {/* Right Column: Upload & Validation */}
            <div className="lg:col-span-8 space-y-6">

              {/* Step 3: Upload Area */}
              <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-6">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-7 h-7 rounded-lg bg-indigo-50 text-indigo-600 flex items-center justify-center text-xs font-bold border border-indigo-100">3</div>
                  <h3 className="font-bold text-gray-900 text-sm">Upload & Validate</h3>
                </div>

                <div
                  {...getRootProps()}
                  className={`relative border-2 border-dashed rounded-xl p-8 text-center transition-all duration-300 group ${!selectedProjectType
                    ? 'border-gray-100 bg-gray-50 cursor-not-allowed'
                    : isDragActive
                      ? 'border-green-400 bg-green-50 scale-[0.99]'
                      : uploadedFile
                        ? 'border-green-200 bg-green-50/30'
                        : 'border-gray-200 hover:border-green-400 hover:bg-gray-50 cursor-pointer'
                    }`}
                >
                  <input {...getInputProps()} />

                  {isProcessingFile ? (
                    <div className="py-8">
                      <Loader2 className="w-10 h-10 text-green-500 animate-spin mx-auto mb-3" />
                      <p className="text-sm font-bold text-gray-900">Processing File...</p>
                      <p className="text-xs text-gray-500">Validating data structure</p>
                    </div>
                  ) : uploadedFile ? (
                    <div className="py-4">
                      <div className="w-12 h-12 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-3">
                        <CheckCircle2 className="w-6 h-6 text-green-600" />
                      </div>
                      <p className="text-sm font-bold text-gray-900">{uploadedFile.name}</p>
                      <p className="text-xs text-gray-500 mb-4">{(uploadedFile.size / 1024).toFixed(1)} KB</p>
                      <div onClick={(e) => e.stopPropagation()}>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={handleReset}
                          className="text-xs h-8 text-red-600 border-red-200 hover:bg-red-50"
                        >
                          Remove File
                        </Button>
                      </div>
                    </div>
                  ) : (
                    <div className="py-8">
                      <div className="w-12 h-12 rounded-full bg-gray-100 flex items-center justify-center mx-auto mb-3 group-hover:scale-110 transition-transform duration-300">
                        <Upload className="w-6 h-6 text-gray-400 group-hover:text-green-500" />
                      </div>
                      <p className="text-sm font-bold text-gray-900">
                        {selectedProjectType ? "Click to Upload Excel File" : "Select Project Type First"}
                      </p>
                      <p className="text-xs text-gray-500 mt-1">or drag and drop here</p>
                    </div>
                  )}
                </div>
              </div>

              {/* Validation Results */}
              <AnimatePresence>
                {validationResult && (
                  <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -10 }}
                    className="space-y-4"
                  >
                    {/* Stats Cards */}
                    <div className="grid grid-cols-3 gap-4">
                      <div className="bg-white p-4 rounded-xl border border-gray-100 shadow-sm flex flex-col items-center justify-center text-center">
                        <span className="text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-1">Total Rows</span>
                        <span className="text-2xl font-bold text-gray-900">{validationResult.totalRows}</span>
                      </div>
                      <div className="bg-green-50 p-4 rounded-xl border border-green-100 shadow-sm flex flex-col items-center justify-center text-center">
                        <span className="text-[10px] font-bold text-green-600 uppercase tracking-wider mb-1">Valid</span>
                        <span className="text-2xl font-bold text-green-700">{validationResult.validCheckpoints.length}</span>
                      </div>
                      <div className={`p-4 rounded-xl border shadow-sm flex flex-col items-center justify-center text-center ${validationResult.invalidCheckpoints.length > 0 ? 'bg-red-50 border-red-100' : 'bg-gray-50 border-gray-100'}`}>
                        <span className={`text-[10px] font-bold uppercase tracking-wider mb-1 ${validationResult.invalidCheckpoints.length > 0 ? 'text-red-600' : 'text-gray-400'}`}>Invalid</span>
                        <span className={`text-2xl font-bold ${validationResult.invalidCheckpoints.length > 0 ? 'text-red-700' : 'text-gray-400'}`}>{validationResult.invalidCheckpoints.length}</span>
                      </div>
                    </div>

                    {/* Invalid List */}
                    {validationResult.invalidCheckpoints.length > 0 && (
                      <div className="bg-red-50/50 rounded-xl border border-red-100 overflow-hidden">
                        <div className="px-4 py-3 border-b border-red-100 bg-red-50 flex items-center justify-between">
                          <h4 className="text-xs font-bold text-red-900 flex items-center gap-2">
                            <AlertTriangle className="w-3.5 h-3.5" />
                            Validation Errors ({validationResult.invalidCheckpoints.length})
                          </h4>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={downloadErrorFile}
                            className="h-7 px-3 text-xs font-bold text-red-700 border-red-300 hover:bg-red-100"
                          >
                            <Download className="w-3 h-3 mr-1.5" />
                            Download Errors (Template Format)
                          </Button>
                        </div>
                        <div className="max-h-60 overflow-y-auto p-2 space-y-2">
                          {validationResult.invalidCheckpoints.map((cp, idx) => (
                            <div key={idx} className="bg-white p-3 rounded-lg border border-red-100 text-xs shadow-sm">
                              <div className="flex justify-between items-start mb-1">
                                <span className="font-bold text-gray-800">Row {cp.rowIndex}: {cp.name || 'Unknown'}</span>
                                <span className="text-red-600 font-bold bg-red-50 px-1.5 py-0.5 rounded">{cp.errors.length} Errors</span>
                              </div>
                              <div className="text-gray-500 mb-2">{cp.assetName} • {cp.frequency}</div>
                              <ul className="space-y-1 pl-4 list-disc text-red-600">
                                {cp.errors.map((err, i) => <li key={i}>{err}</li>)}
                              </ul>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Valid List Preview */}
                    {validationResult.validCheckpoints.length > 0 && (
                      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden shadow-sm">
                        <div className="px-4 py-3 border-b border-gray-100 flex items-center justify-between bg-gray-50/50">
                          <h4 className="text-xs font-bold text-gray-900 flex items-center gap-2">
                            <CheckCircle2 className="w-3.5 h-3.5 text-green-500" />
                            Valid Checkpoints Preview
                          </h4>
                          <span className="text-[10px] text-gray-500 font-medium">Showing first 10 of {validationResult.validCheckpoints.length}</span>
                        </div>
                        <div className="overflow-x-auto">
                          <table className="w-full text-xs text-left">
                            <thead className="bg-gray-50 text-gray-500 font-semibold border-b border-gray-100">
                              <tr>
                                <th className="px-4 py-2">Description</th>
                                <th className="px-4 py-2">Frequency</th>
                                <th className="px-4 py-2">Asset</th>
                                <th className="px-4 py-2">Task Type</th>
                                <th className="px-4 py-2">Phase</th>
                              </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-100">
                              {validationResult.validCheckpoints.slice(0, 10).map((cp, idx) => (
                                <tr key={idx} className="hover:bg-gray-50/50">
                                  <td className="px-4 py-2 font-medium text-gray-900 truncate max-w-[200px]">{cp.name}</td>
                                  <td className="px-4 py-2 text-gray-600">
                                    <span className="bg-blue-50 text-blue-700 px-1.5 py-0.5 rounded border border-blue-100">{cp.frequency}</span>
                                  </td>
                                  <td className="px-4 py-2 text-gray-600">{cp.assetName}</td>
                                  <td className="px-4 py-2 text-gray-600">{cp.taskType || '-'}</td>
                                  <td className="px-4 py-2 text-gray-600">{cp.executionShift || '-'}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    )}

                    {/* Submit Actions */}
                    <div className="flex justify-end gap-3 pt-2">
                      <Button
                        variant="outline"
                        onClick={handleReset}
                        disabled={isSubmitting}
                        className="h-10 px-6 text-xs font-bold"
                      >
                        Reset
                      </Button>
                      <Button
                        variant="sorigin"
                        onClick={handleSubmit}
                        disabled={isSubmitting || validationResult.validCheckpoints.length === 0}
                        className="h-10 px-6 text-xs font-bold min-w-[150px] bg-green-600 hover:bg-green-700 shadow-sm shadow-green-200"
                      >
                        {isSubmitting ? (
                          <>
                            <Loader2 className="w-3.5 h-3.5 animate-spin mr-2" />
                            Creating...
                          </>
                        ) : (
                          <>
                            <Save className="w-3.5 h-3.5 mr-2" />
                            Create {validationResult.validCheckpoints.length} Checkpoint{validationResult.validCheckpoints.length !== 1 ? 's' : ''}
                            {validationResult.invalidCheckpoints.length > 0 && ` (${validationResult.invalidCheckpoints.length} invalid skipped)`}
                          </>
                        )}
                      </Button>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          </div>
        </motion.div>
      </div>
    </div >
  );
}